import {
    doc,
    getDoc,
    setDoc,
    deleteDoc,
    collection,
    getDocs,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface NegativePrompt {
    shopId: string;
    shopName: string;
    content: string;
    updatedAt: Date;
    updatedBy?: string;
}

const NEGATIVE_PROMPTS_COLLECTION = 'negativePrompts';

/**
 * Get negative prompt for a specific shop
 */
export async function getNegativePrompt(shopId: string): Promise<NegativePrompt | null> {
    try {
        const docRef = doc(db, NEGATIVE_PROMPTS_COLLECTION, shopId);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            return null;
        }

        const data = docSnap.data();
        return {
            shopId: docSnap.id,
            shopName: data.shopName || '',
            content: data.content || '',
            updatedAt: data.updatedAt?.toDate() || new Date(),
            updatedBy: data.updatedBy,
        };
    } catch (error) {
        console.error('Error fetching negative prompt:', error);
        throw error;
    }
}

/**
 * Get all negative prompts (for listing in PromptsPage)
 */
export async function getAllNegativePrompts(): Promise<NegativePrompt[]> {
    try {
        const collectionRef = collection(db, NEGATIVE_PROMPTS_COLLECTION);
        const snapshot = await getDocs(collectionRef);

        const prompts: NegativePrompt[] = [];

        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            prompts.push({
                shopId: docSnap.id,
                shopName: data.shopName || '',
                content: data.content || '',
                updatedAt: data.updatedAt?.toDate() || new Date(),
                updatedBy: data.updatedBy,
            });
        });

        // Sort by shopName
        prompts.sort((a, b) => a.shopName.localeCompare(b.shopName));

        return prompts;
    } catch (error) {
        console.error('Error fetching all negative prompts:', error);
        throw error;
    }
}

/**
 * Save (create or update) negative prompt for a shop
 */
export async function saveNegativePrompt(
    shopId: string,
    shopName: string,
    content: string,
    updatedBy?: string
): Promise<void> {
    try {
        const docRef = doc(db, NEGATIVE_PROMPTS_COLLECTION, shopId);

        await setDoc(docRef, {
            shopName,
            content,
            updatedAt: Timestamp.now(),
            updatedBy,
        });
    } catch (error) {
        console.error('Error saving negative prompt:', error);
        throw error;
    }
}

/**
 * Delete negative prompt for a shop
 */
export async function deleteNegativePrompt(shopId: string): Promise<void> {
    try {
        const docRef = doc(db, NEGATIVE_PROMPTS_COLLECTION, shopId);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting negative prompt:', error);
        throw error;
    }
}
